import cloneDeep from 'lodash/cloneDeep';

import { ConditionGroup, DesignData, MediaElement, ReflowGroup, SlotLayout } from '../types';

import { getInterchangeableSpreadIndexes } from './spreadShape';

// One slot's artwork: the elements a user placed, and the state describing how
// they are arranged and personalized. Everything else describes the surface
// being printed and stays where it is — see spreadShape.
interface SpreadContent {
  pages: Array<{ media?: MediaElement[]; backgroundColor?: string }>;
  // Element-addressed by uuid, so it has to follow the elements it refers to.
  conditionGroup?: ConditionGroup;
  reflowGroups?: ReflowGroup[];
  // The elements carry its frame ids, so it follows them like the condition group.
  slotLayouts?: SlotLayout[];
  // Names the arrangement of the elements below it, which carry matching frame
  // ids, so it belongs to the artwork rather than the slot.
  layoutSchemaName?: string;
}

// A single artwork move: the slot to read from and the slot to write to.
interface SpreadContentMove {
  fromSpreadIndex: number;
  toSpreadIndex: number;
}

// Turns a drag into the moves it implies: the dragged artwork lands on the
// destination and everything between shifts one slot to make room.
//
// `eligibleSpreadIndexes` is the ordered list of slots the drag happened in, so
// the result matches what the user manipulated rather than the underlying array
// order, and slots absent from it are never disturbed.
function getSpreadContentMoves(
  design: DesignData,
  fromSpreadIndex: number,
  toSpreadIndex: number,
  eligibleSpreadIndexes: number[],
): SpreadContentMove[] {
  const slots = getInterchangeableSpreadIndexes(design, fromSpreadIndex, eligibleSpreadIndexes);
  const fromPosition = slots.indexOf(fromSpreadIndex);
  const toPosition = slots.indexOf(toSpreadIndex);

  if (fromPosition === -1 || toPosition === -1 || fromPosition === toPosition) {
    return [];
  }

  const order = [...slots];
  const [movedSlot] = order.splice(fromPosition, 1);
  order.splice(toPosition, 0, movedSlot);

  return order.reduce<SpreadContentMove[]>((moves, sourceSlot, position) => {
    const targetSlot = slots[position];
    if (sourceSlot !== targetSlot) {
      moves.push({ fromSpreadIndex: sourceSlot, toSpreadIndex: targetSlot });
    }
    return moves;
  }, []);
}

function extractSpreadContent(design: DesignData, spreadIndex: number): SpreadContent {
  const spread = design.spreads[spreadIndex];

  // Cloned because a move is a permutation: the same values are read out of one
  // slot and written into another within a single draft, and cloning keeps two
  // slots from sharing a subtree.
  return cloneDeep({
    pages: spread.pages.map((page) => ({ media: page.groups.media, backgroundColor: page.backgroundColor })),
    conditionGroup: spread.conditionGroup,
    reflowGroups: spread.reflowGroups,
    slotLayouts: spread.slotLayouts,
    layoutSchemaName: spread.layoutSchemaName,
  });
}

function assignOrDelete<T, K extends keyof T>(target: T, key: K, value: T[K] | undefined) {
  if (value === undefined) {
    delete target[key];
  } else {
    target[key] = value;
  }
}

function applySpreadContent(design: DesignData, spreadIndex: number, content: SpreadContent) {
  const spread = design.spreads[spreadIndex];

  spread.pages.forEach((page, pageIndex) => {
    const pageContent = content.pages[pageIndex];
    if (!pageContent) {
      return;
    }

    assignOrDelete(page.groups, 'media', pageContent.media);
    assignOrDelete(page, 'backgroundColor', pageContent.backgroundColor);
  });

  assignOrDelete(spread, 'conditionGroup', content.conditionGroup);
  assignOrDelete(spread, 'reflowGroups', content.reflowGroups);
  assignOrDelete(spread, 'slotLayouts', content.slotLayouts);
  assignOrDelete(spread, 'layoutSchemaName', content.layoutSchemaName);
}

// Applies the moves produced by getSpreadContentMoves, leaving the spreads array
// itself untouched. Because slots keep their position, everything keyed by
// spread index stays valid and no page numbering has to be recomputed.
function moveSpreadContentUtil(design: DesignData, moves: SpreadContentMove[]): boolean {
  // The moves describe a permutation, so applying only some of them would copy
  // artwork onto a slot rather than rotate it — leaving two slots holding the
  // same elements and losing whatever the dropped move was carrying. Either all
  // of them are applicable or none are.
  const applicable = moves.every(
    ({ fromSpreadIndex, toSpreadIndex }) => design.spreads[fromSpreadIndex] && design.spreads[toSpreadIndex],
  );

  if (!moves.length || !applicable) {
    return false;
  }

  // Read every slot before writing any of them: sources and targets overlap.
  const contents = moves.map(({ fromSpreadIndex }) => extractSpreadContent(design, fromSpreadIndex));

  moves.forEach((move, index) => {
    applySpreadContent(design, move.toSpreadIndex, contents[index]);
  });

  return true;
}

export type { SpreadContentMove };
export { getSpreadContentMoves };
export default moveSpreadContentUtil;
